import { CompanionCard } from '@components/Cards';
import Loader from '@components/Loader';
import { useAllCompanionsInfiniteScroll } from '@hooks/useAllCompanionsInfiniteScroll';
import { showToast } from '@utils/toast';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import {
  addFavouriteCompanion,
  getFavouriteCompanionsPaginated,
  removeFavouriteCompanion,
} from '../../services/api';
import { Companion } from '../../types';

export const CompanionsPage = memo(() => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();

  // State for tracking favorited companions
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

  // Use infinite scroll for ALL companions (featured + randomly sorted regular)
  const {
    companions,
    loading: companionsLoading,
    error,
    hasMore,
    sentinelRef,
  } = useAllCompanionsInfiniteScroll({
    rootMargin: '200px',
    enabled: true,
    scrollContainerRef,
  });

  // Fetch user's favorited companions on mount (if authenticated)
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!isAuthenticated) return;
      try {
        // Fetch all favorites (using a large limit to get all)
        const result = await getFavouriteCompanionsPaginated(100, 0);
        // Backend returns `id` which IS the companionId (= mentorId)
        const ids = new Set(
          result.items
            .map((item: any) => item.id)
            .filter((id: string | undefined) => id),
        );
        setFavoritedIds(ids);
      } catch (error) {
        console.error('Error fetching favorite companions:', error);
      }
    };
    fetchFavorites();
  }, [isAuthenticated]);

  // Handle favorite toggle
  const handleFavoriteToggle = useCallback(
    async (companion: Companion) => {
      // Use mentorId as companionId (backend expects mentorId as companionId)
      const companionId = companion.mentorId;
      if (!companionId) return;

      setLoadingIds((prev) => new Set(prev).add(companionId));

      try {
        if (favoritedIds.has(companionId)) {
          // Remove from favorites - DELETE /api/favorites/companions/{companionId}
          await removeFavouriteCompanion(companionId);
          setFavoritedIds((prev) => {
            const next = new Set(prev);
            next.delete(companionId);
            return next;
          });
          showToast.success(
            t('companionsPage.removedFromFavorites', { name: companion.firstName || 'Companion' }),
          );
        } else {
          // Add to favorites - POST with { companionId: mentorId, slug }
          await addFavouriteCompanion(companionId, companion.slug || '');
          setFavoritedIds((prev) => new Set(prev).add(companionId));
          showToast.success(
            t('companionsPage.addedToFavorites', { name: companion.firstName || 'Companion' }),
          );
        }
      } catch (error) {
        console.error('Error toggling favorite:', error);
        showToast.error(t('companionsPage.failedToUpdateFavorite'));
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(companionId);
          return next;
        });
      }
    },
    [favoritedIds],
  );

  return (
    <div className="flex flex-col h-full pt-0 sm:pt-[10px]">
      {/* Fixed Header */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between px-4 sm:px-6">
          <div className="flex flex-col">
            <h1 className="text-[32px] sm:text-[40px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
              {t('companionsPage.title')}
            </h1>
            <div className="text-[14px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
              {t('companionsPage.subtitle')}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto na-scroll-container">
        <div className="px-4 sm:px-6 pb-[220px]">
          {/* Loading State - only show when page is active and no companions and loading */}
          {companionsLoading && companions.length === 0 && (
            <div className="responsive-grid gap-4">
              {[...Array(8)].map((_, index) => (
                <div
                  key={`loading-${index}`}
                  className="flex flex-col animate-pulse">
                  <div className="w-full aspect-square rounded-[8px] bg-gray-700 mb-3"></div>
                  <div className="h-4 bg-gray-700 rounded mb-2"></div>
                  <div className="h-3 bg-gray-600 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-600 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          )}

          {/* Error State - only show when there's an error and no companions */}
          {error && companions.length === 0 && (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <div className="text-red-400 mb-4">
                  <svg
                    className="w-8 h-8 mx-auto mb-2"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-sm font-medium">
                    {t('companionsPage.failedToLoad')}
                  </p>
                  <p className="text-xs text-red-300 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          {companions.length > 0 && (
            <div className="responsive-grid gap-4">
              {companions.map((companion: Companion, index: number) => {
                const companionKey =
                  companion.mentorId || companion.slug || index.toString();

                return (
                  <CompanionCard
                    key={`companion-${companionKey}`}
                    companion={companion}
                    showFavoriteButton={isAuthenticated}
                    isFavorited={
                      companion.mentorId
                        ? favoritedIds.has(companion.mentorId)
                        : false
                    }
                    isLoading={
                      companion.mentorId
                        ? loadingIds.has(companion.mentorId)
                        : false
                    }
                    onFavoriteClick={(e) => {
                      e.stopPropagation();
                      handleFavoriteToggle(companion);
                    }}
                  />
                );
              })}

              {/* Infinite scroll sentinel - inside grid with proper column span */}
              {hasMore && (
                <div
                  ref={sentinelRef}
                  className="col-span-1 md:col-span-2 lg:col-span-3 xl:col-span-4 flex justify-center py-8">
                  {companionsLoading ? (
                    <Loader size="medium" text={t('companionsPage.loadingMore')} />
                  ) : (
                    <div className="w-full h-4"></div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Error message for loading more items */}
          {error && companions.length > 0 && (
            <div className="flex justify-center mt-4">
              <div className="text-red-400 text-center">
                <p className="text-sm">{t('companionsPage.failedToLoadMore')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

CompanionsPage.displayName = 'CompanionsPage';
